import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Sort, SortDirection } from '@angular/material/sort';
import { Observable } from 'rxjs';
import { ERoutesApi, FilterType } from '@core/enums';
import { CrudService, FilterService, MessageService } from '@core/services';
import { PageFilter, PageRequest } from '@core/types';
import { FilterInputType } from '@shared/components';
import { downloadFile, MessagesUtil, openFileInNewTab } from '@core/utils';

export type FilterOption = {
  label: string;
  value: any;
};

export type HeaderFilterConfig = {
  enabled?: boolean;
  type?: FilterType;
  placeholder?: string;
  options?: FilterOption[];
  options$?: Observable<FilterOption[]>;
};

export type FilterChip = {
  label: string;
  attribute: string;
  displayValue: string;
};

export type HeaderOptions = {
  description: string;
  attribute: string;
  attributeSelectorCheckbox?: string;
  mappedFor?: string;
  type?: HeaderOptionsTypes;
  typeFilter?: HeaderOptionsFilterTypes;
  isToFormatValue?: boolean;
  digitsInfo?: string;
  route?: string;
  filter?: HeaderFilterConfig;
  sortable?: boolean;
};

export enum HeaderOptionsTypes {
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  DATE_TIME = 'DATE_TIME',
  DATE_TIME_MILLISECONDS = 'DATE_TIME_MILLISECONDS',
  TIME = 'TIME'
}

export enum HeaderOptionsFilterTypes {
  INPUT = 'INPUT',
  SELECTOR_CHECKBOX = 'SELECTOR_CHECKBOX'
}

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_ATTRIBUTE = '__search__';

function resolveFilterType(header: HeaderOptions | undefined, attribute: string): FilterType {
  if (header?.filter?.type) return header.filter.type;
  if (attribute === 'id') return FilterType.NUMBER;
  return FilterType.STRING;
}

function filterTypeToPageFilterType(type: FilterType): PageFilter['type'] {
  if (type === FilterType.DATE || type === FilterType.DATE_RANGE) return 'date';
  if (type === FilterType.NUMBER) return 'number';
  return 'string';
}

function filterTypeToOperator(type: FilterType): PageFilter['operator'] {
  if (type === FilterType.SELECT || type === FilterType.SELECT_ASYNC) return 'in';
  if (type === FilterType.DATE) return 'equal';
  if (type === FilterType.DATE_RANGE) return 'between';
  return 'contains';
}

function formatDateChip(value: string): string {
  const [y, m, d] = value.split('-');
  return `${d}/${m}/${y}`;
}

@Component({
  selector: 'table-component',
  templateUrl: 'table.component.html',
  styleUrls: ['table.style.scss']
})
export class TableComponent implements OnInit, OnChanges, OnDestroy {

  digitsInfoDefault = '1.4-4';

  actualSort: Sort;
  beforeSort: Sort;
  isToSort = true;

  columnFilterValues = new Map<string, any>();

  query = '';
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;

  pageRequest: PageRequest;

  private destroyed = false;

  @Input() rota: string;
  @Input() data: any[];
  @Input() totalItems: number;
  @Input() isLoading: boolean;
  @Input() headerOptions: HeaderOptions[];
  @Input() subtitleFooter: string;
  @Input() pageOptions = [10, 15, 20, 25, 30];
  @Input() paginaAtual = 0;
  @Input() pageSize = DEFAULT_PAGE_SIZE;
  @Input() fileNameExport: string;
  @Input() pathExport: string;
  @Input() pathExportCustomized: boolean;
  @Input() paramsToExportFile: Map<string, string>;
  @Input() selectionCheckbox = new SelectionModel<any>(true, []);
  @Input() hasBadgeImport = false;
  @Input() hasImport = false;
  @Input() hasCreate = false;
  @Input() hasDetail = false;
  @Input() hasEdit = false;
  @Input() hasDelete = false;
  @Input() hasValidate = false;
  @Input() hasHistory = false;
  @Input() hasOtherAction = false;
  @Input() hasExportFile = false;
  @Input() hasExport = false;
  @Input() hasRelatorio = false;
  @Input() exportPath: string;
  @Input() showSearch = false;
  @Input() badgeImport: number;
  @Input() titleButtonImport = 'Importar';
  @Input() titleButtonCreate = 'Inserir';
  @Input() titleButtonEdit = 'Editar';
  @Input() titleButtonDelete = 'Deletar';
  @Input() titleButtonDetail = 'Visualizar';
  @Input() titleButtonValidate = 'Validar';
  @Input() titleButtonOtherAction = 'Outro';
  @Input() iconButtonValidate = '';
  @Input() iconButtonOtherAction = '';
  @Input() colorButtonValidate = '';
  @Input() colorButtonOtherAction = '';
  @Input() isSortDisable = false;
  @Input() isPaginateDisable = false;
  @Input() isHeaderLarge = false;
  @Input() hasSelectionCheckbox = false;
  @Input() hasColumnComentarios = false;
  @Input() tipoTabela!: string;

  @Output() handleOnClickRow   = new EventEmitter<any>();
  @Output() handleCreate       = new EventEmitter<void>();
  @Output() handleImport       = new EventEmitter<void>();
  @Output() handleDetail       = new EventEmitter<any>();
  @Output() handleEdit         = new EventEmitter<any>();
  @Output() handleDelete       = new EventEmitter<any>();
  @Output() handleValidate     = new EventEmitter<any>();
  @Output() handleHistory      = new EventEmitter<any>();
  @Output() handleOtherAction  = new EventEmitter<any>();
  @Output() handleClearFilters = new EventEmitter<void>();
  @Output() handleSearchData   = new EventEmitter<PageRequest>();
  @Output() handleRelatorio    = new EventEmitter<void>();

  constructor(
    private readonly filterService: FilterService,
    private readonly crudService: CrudService<any>,
    private readonly messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.initPageRequest();
    if (!this.data?.length) {
      this.handleSearchData.emit(this.pageRequest);
    }
  }

  ngOnChanges(): void {
    if (this.data?.length > 0 && this.rota) {
      this.updateSelectionCheckbox();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  }

  handleExportar(formato: 'excel' | 'pdf'): void {
    this.messageService.showLoading(MessagesUtil.get('tabela.arquivo.gerando'));

    this.crudService.postBlobWithFilename(`${this.exportPath}/exportar/${formato}`, this.pageRequest)
      .subscribe({
        next: ({ blob, filename }) => {
          this.messageService.clearLoading();
          formato === 'pdf' ? openFileInNewTab(blob) : downloadFile(blob, filename);
          this.messageService.showSuccess(MessagesUtil.get('tabela.sucesso.arquivo.gerado'));
        },
        error: () => {
          this.messageService.clearLoading();
          this.messageService.showError(MessagesUtil.get('tabela.erro.gerar.arquivo'));
        }
      });
  }

  onSearchInput(value: string): void {
    this.query = value;
    if (this.searchTimeout) clearTimeout(this.searchTimeout);

    if (!value) {
      this.removeSearchFilter();
      return;
    }

    this.searchTimeout = setTimeout(() => {
      this.applySearchFilter(value);
      this.searchTimeout = null;
    }, 800);
  }

  handleClearQuery(): void {
    this.query = '';
    this.removeSearchFilter();
  }

  getFilterType(header: HeaderOptions): FilterType {
    if (header.filter?.enabled === false) return FilterType.NONE;
    return header.filter?.type ?? FilterType.STRING;
  }

  getFilterInputType(header: HeaderOptions): FilterInputType {
    return (header.filter?.type ?? FilterType.STRING) as FilterInputType;
  }

  onColumnFilterChange(attribute: string, value: any): void {
    this.setColumnFilter(attribute, value);
  }

  get activeFilterChips(): FilterChip[] {
    return Array.from(this.columnFilterValues.entries())
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([attribute, value]) => {
        const header = this.headerOptions?.find(h => h.attribute === attribute);
        return {
          label: header?.description ?? attribute,
          attribute,
          displayValue: this.formatChipDisplayValue(value, header)
        };
      });
  }

  removeFilterChip(attribute: string): void {
    const updated = new Map(this.columnFilterValues);

    updated.delete(attribute);

    this.columnFilterValues = updated;

    this.syncFiltersAndEmit();
  }

  clearAllColumnFilters(): void {
    this.columnFilterValues = new Map();

    this.syncFiltersAndEmit();

    this.handleClearFilters.emit();
  }

  get totalPagesCalc(): number {
    return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
  }

  get paginationNums(): number[] {
    const total = this.totalPagesCalc;
    const current = this.paginaAtual + 1;
    const maxLinks = 5;

    let start = Math.max(1, current - Math.floor(maxLinks / 2));
    let end = start + maxLinks - 1;

    if (end > total) {
      end = total;
      start = Math.max(1, end - maxLinks + 1);
    }

    const nums: number[] = [];
    for (let i = start; i <= end; i++) nums.push(i);
    return nums;
  }

  goToPage(page: number | string): void {
    const normalizedPage = Math.max(0, Math.min(this.totalPagesCalc - 1, +page - 1));
    this.paginaAtual = normalizedPage;
    this.pageRequest = { ...this.pageRequest, page: normalizedPage };
    this.persist();
    this.handleSearchData.emit(this.pageRequest);
  }

  goToFirstPage(): void { this.goToPage(1); }
  goToLastPage(): void { this.goToPage(this.totalPagesCalc); }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.paginaAtual = 0;
    this.pageRequest = { ...this.pageRequest, page: 0, size };
    this.persist();
    this.handleSearchData.emit(this.pageRequest);
  }

  get _headerOptionsAttributes(): string[] {
    const headers: string[] = [];
    if (this.hasAnyAction()) headers.push('acoes');
    if (this.hasSelectionCheckbox) headers.push('checkbox');
    headers.push(...this.headerOptions.map(h => h.attribute));
    if (this.hasColumnComentarios) headers.push('comentarios');
    headers.push('fim');
    return headers;
  }

  hasAnyAction(): boolean {
    return (
      this.hasDetail ||
      this.hasEdit ||
      this.hasDelete ||
      this.hasValidate ||
      this.hasOtherAction ||
      this.data?.some(e => e.display_info_criacao !== null)
    );
  }

  isAllSelected(): boolean {
    return this.selectionCheckbox.selected.length === this.data.length;
  }

  toggleAllRows(): void {
    this.isAllSelected()
      ? this.selectionCheckbox.clear()
      : this.selectionCheckbox.select(...this.data);
  }

  toggleOneRow(row: any): void {
    this.selectionCheckbox.toggle(row);
  }

  trackByFn(index: number, item: any): any {
    return item?.id ?? index;
  }

  handleSort(sort: Sort): void {
    const { active, direction } = sort;
    this.actualSort = sort;

    const filtersWithoutOrder = this.stripOrderFromFilters();

    if (this.isToSort || active !== this.beforeSort?.active) {
      this.isToSort = !(direction === 'desc' && active === this.beforeSort?.active);
      if (direction) this.applyOrderToFilters(filtersWithoutOrder, active, direction);
      this.pageRequest = { ...this.pageRequest, filters: filtersWithoutOrder };
    } else {
      this.isToSort = true;
      this.actualSort = null;
      this.pageRequest = { ...this.pageRequest, filters: filtersWithoutOrder };
    }

    this.persist();
    this.handleSearchData.emit(this.pageRequest);
    this.beforeSort = sort;
  }

  handleCheckSortActive(): string {
    if (this.isSortDisable) return '';
    return this.actualSort?.active ?? 'asc';
  }

  handleCheckSortDirection(): SortDirection {
    if (this.isSortDisable) return '';
    return (this.actualSort?.direction as SortDirection) ?? 'asc';
  }

  emitClickRow(element: any): void  { this.handleOnClickRow.emit(element); }
  emitCreate(): void                { this.handleCreate.emit(); }
  emitImport(): void                { this.handleImport.emit(); }
  emitDetail(element: any): void    { this.handleDetail.emit(element); }
  emitEdit(element: any): void      { this.handleEdit.emit(element); }
  emitDelete(element: any): void    { this.handleDelete.emit({ id: element, pageRequest: this.pageRequest }); }
  emitValidate(element: any): void  { this.handleValidate.emit(element); }
  emitHistory(element: any): void   { this.handleHistory.emit(element); }
  emitOtherAction(element: any): void { this.handleOtherAction.emit({ data: element, pageRequest: this.pageRequest }); }

  private applySearchFilter(value: string): void {
    const searchAttributes = this.headerOptions
      .filter(h => h.filter?.enabled !== false)
      .map(h => h.attribute);

    const searchFilter: PageFilter = {
      attribute: SEARCH_ATTRIBUTE,
      operator: 'search',
      type: 'string',
      values: [value],
      searchAttributes,
      label: MessagesUtil.get('tabela.pesquisa'),
      displayValues: [value]
    };

    const orderOnlyFilters = this.pageRequest.filters.filter(
      f => f.order && !this.columnFilterValues.has(f.attribute)
    );

    this.pageRequest = {
      ...this.pageRequest,
      page: 0,
      filters: [...orderOnlyFilters, searchFilter, ...this.buildEnrichedColumnFilters()]
    };
    this.persist();
    this.handleSearchData.emit(this.pageRequest);
  }

  private removeSearchFilter(): void {
    this.pageRequest = { ...this.pageRequest, page: 0, filters: this.stripSearchFilter() };
    this.persist();
    this.handleSearchData.emit(this.pageRequest);
  }

  private stripSearchFilter(): PageFilter[] {
    return this.pageRequest.filters.filter(f => f.attribute !== SEARCH_ATTRIBUTE);
  }

  private setColumnFilter(attribute: string, value: any): void {
    const updated = new Map(this.columnFilterValues);

    if (value === null || value === undefined || value === '') {
      updated.delete(attribute);
    } else {
      updated.set(attribute, value);
    }

    this.columnFilterValues = updated;

    this.syncFiltersAndEmit();
  }

  private syncFiltersAndEmit(): void {
    this.paginaAtual = 0;

    const orderFilters = this.pageRequest.filters
      .filter(f => f.order)
      .map(f => ({ ...f, values: [], operator: 'contains' as PageFilter['operator'] }));

    const searchFilter = this.pageRequest.filters.filter(f => f.attribute === SEARCH_ATTRIBUTE);

    const enrichedColumnFilters = this.buildEnrichedColumnFilters();

    const mergedFilters = orderFilters.map(orderFilter => {
      const columnFilter = enrichedColumnFilters.find(cf => cf.attribute === orderFilter.attribute);
      return columnFilter
        ? { ...columnFilter, order: orderFilter.order }
        : orderFilter;
    });

    const columnFiltersWithoutOrder = enrichedColumnFilters.filter(
      cf => !orderFilters.some(of => of.attribute === cf.attribute)
    );

    this.pageRequest = {
      ...this.pageRequest,
      page: 0,
      filters: [...mergedFilters, ...searchFilter, ...columnFiltersWithoutOrder]
    };

    this.persist();
    this.handleSearchData.emit(this.pageRequest);
  }

  private buildEnrichedColumnFilters(): PageFilter[] {
    return Array.from(this.columnFilterValues.entries()).map(([attribute, value]) => {
      const header = this.headerOptions?.find(h => h.attribute === attribute);
      const filterType = resolveFilterType(header, attribute);
      const existingOrder = this.pageRequest.filters.find(f => f.attribute === attribute)?.order;
      const rawValues = Array.isArray(value) ? value.map(String) : [String(value)];
      const displayValues = this.buildDisplayValues(filterType, rawValues, header);

      return {
        attribute,
        operator: filterTypeToOperator(filterType),
        type: filterTypeToPageFilterType(filterType),
        values: rawValues,
        label: header?.description ?? attribute,
        displayValues,
        ...(existingOrder ? { order: existingOrder } : {})
      };
    });
  }

  private buildDisplayValues(type: FilterType, rawValues: string[], header?: HeaderOptions): string[] {
    if (type === FilterType.SELECT || type === FilterType.SELECT_ASYNC) {
      return rawValues.map(v => header?.filter?.options?.find(o => o.value === v)?.label ?? v);
    }
    if (type === FilterType.DATE) {
      return rawValues.map(v => formatDateChip(v));
    }
    if (type === FilterType.DATE_RANGE && rawValues.length === 2) {
      return [`${formatDateChip(rawValues[0])} até ${formatDateChip(rawValues[1])}`];
    }
    return rawValues;
  }

  private formatChipDisplayValue(value: any, header?: HeaderOptions): string {
    const type = header?.filter?.type ?? FilterType.STRING;

    if (type === FilterType.SELECT)
      return header.filter?.options?.find(o => o.value === value)?.label ?? String(value);
    if (type === FilterType.SELECT_ASYNC)
      return String(value);
    if (type === FilterType.DATE && value)
      return formatDateChip(String(value));
    if (type === FilterType.DATE_RANGE && Array.isArray(value) && value.length === 2)
      return `${formatDateChip(value[0])} até ${formatDateChip(value[1])}`;

    return String(value);
  }

  private stripOrderFromFilters(): PageFilter[] {
    return this.pageRequest.filters.map(f => ({ ...f, order: undefined }));
  }

  private applyOrderToFilters(filters: PageFilter[], attribute: string, direction: string): void {
    const order: 'ASC' | 'DESC' = direction === 'desc' ? 'DESC' : 'ASC';
    const existing = filters.find(f => f.attribute === attribute);
    if (existing) {
      existing.order = order;
    } else {
      filters.push({ attribute, operator: 'contains', type: 'string', values: [], order });
    }
  }

  private initPageRequest(): void {
    if (!this.rota) {
      this.pageRequest = { page: 0, size: this.pageSize, filters: [] };
      return;
    }

    const cached = this.filterService.recoverFilter(this.rota);

    if (cached) {
      this.pageRequest = cached;
      this.paginaAtual = cached.page;
      this.pageSize = cached.size;
      this.restoreColumnFilterValues();
      this.restoreSort();
      this.restoreSearchQuery();
    } else {
      this.pageRequest = { page: 0, size: this.pageSize, filters: [] };
      this.persist();
    }
  }

  private restoreColumnFilterValues(): void {
    this.columnFilterValues = new Map(
      this.pageRequest.filters
        .filter(f => f.values?.length > 0 && f.attribute !== SEARCH_ATTRIBUTE)
        .map(f => [f.attribute, f.values.length === 1 ? f.values[0] : f.values])
    );
  }

  private restoreSort(): void {
    const sortFilter = this.pageRequest.filters.find(f => f.order);
    if (sortFilter && !this.isSortDisable) {
      const direction: SortDirection = sortFilter.order === 'DESC' ? 'desc' : 'asc';
      this.actualSort = { active: sortFilter.attribute, direction };
      this.beforeSort = { active: sortFilter.attribute, direction };
    }
  }

  private restoreSearchQuery(): void {
    const searchFilter = this.pageRequest.filters.find(f => f.attribute === SEARCH_ATTRIBUTE);
    if (searchFilter?.values?.length) {
      this.query = searchFilter.values[0];
    }
  }

  private persist(): void {
    if (this.rota && !this.destroyed) {
      this.filterService.saveFilter(this.rota, this.pageRequest);
    }
  }

  private updateSelectionCheckbox(): void {
    this.selectionCheckbox.clear();
  }

  protected readonly ERoutesApi = ERoutesApi;
}
